import { Head, Link, useForm, usePage } from '@inertiajs/react'
import { FormEvent } from 'react'
import { ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react'
import NavigationRail from '@/components/NavigationRail'
import { FilterState, FilterableField } from '@/components/filters/types'
import PostFormLayout from '@/components/outbound/PostFormLayout'
import ChatFormLayout from '@/components/outbound/ChatFormLayout'
import EmailFormLayout from '@/components/outbound/EmailFormLayout'

interface OutboundMessage {
  id: number
  name: string
  message_type: 'chat' | 'post' | 'email'
  delivery_style: 'badge' | 'snippet' | 'full'
  title: string | null
  body: string
  button_text: string | null
  button_url: string | null
  audience_type: 'dynamic' | 'fixed'
  audience_rules: FilterState | null
  trigger_type: 'event' | 'scheduled' | 'page' | 'all_pages'
  trigger_event: string | null
  trigger_page_rules: PageRule[] | null
  scheduled_at: string | null
  delay_seconds: number
  frequency_cap: number | null
  priority: number
  collect_email: boolean
  status: string
  sent_by_agent: Agent | null
  post_format: 'small' | 'large' | null
  reply_type: 'none' | 'reactions' | 'text'
  reaction_emojis: string[]
  // Email-specific fields
  email_subject: string | null
  email_from_name: string | null
  email_reply_to: string | null
}

interface PageRule {
  match_type: 'exact' | 'contains' | 'starts_with' | 'regex'
  value: string
}

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
}

interface FieldConfig {
  type: string
  column?: string
  computed?: boolean
  metadata_key?: string
  custom?: boolean
  label?: string
  list_options?: string[]
}

interface DataAttributeDef {
  name: string
  label: string
  data_type: string
  list_options: string[] | null
  custom: boolean
}

interface OutboundMessagesFormProps {
  outbound_message: OutboundMessage | null
  filterable_fields: Record<string, FieldConfig>
  data_attributes: DataAttributeDef[]
  currentAgent: Agent
  agents: Agent[]
  // Mirrors of OutboundMessage::DEFAULT_REACTION_EMOJIS / MAX_REACTION_EMOJIS
  default_reaction_emojis: string[]
  max_reaction_emojis: number
  flash?: {
    notice?: string
    alert?: string
  }
  [key: string]: unknown
}

const TEXT_OPERATORS = [
  { value: 'is', label: 'é' },
  { value: 'is_not', label: 'não é' },
  { value: 'contains', label: 'contém' },
  { value: 'does_not_contain', label: 'não contém' },
  { value: 'starts_with', label: 'começa com' },
  { value: 'ends_with', label: 'termina com' },
  { value: 'is_unknown', label: 'é desconhecido' },
  { value: 'has_any_value', label: 'tem algum valor' },
]

const DATE_OPERATORS = [
  { value: 'more_than_days_ago', label: 'há mais de X dias', inputType: 'days_ago' as const },
  { value: 'exactly_days_ago', label: 'há exatamente X dias', inputType: 'days_ago' as const },
  { value: 'less_than_days_ago', label: 'há menos de X dias', inputType: 'days_ago' as const },
  { value: 'after', label: 'depois de', inputType: 'date' as const },
  { value: 'on', label: 'em', inputType: 'date' as const },
  { value: 'before', label: 'antes de', inputType: 'date' as const },
  { value: 'is_unknown', label: 'é desconhecido' },
  { value: 'has_any_value', label: 'tem algum valor' },
]

const NUMERIC_OPERATORS = [
  { value: 'greater_than', label: 'maior que' },
  { value: 'less_than', label: 'menor que' },
  { value: 'is', label: 'é' },
  { value: 'is_not', label: 'não é' },
  { value: 'is_unknown', label: 'é desconhecido' },
  { value: 'has_any_value', label: 'tem algum valor' },
]

const BOOLEAN_OPERATORS = [
  { value: 'is_true', label: 'é verdadeiro' },
  { value: 'is_false', label: 'é falso' },
  { value: 'is_unknown', label: 'é desconhecido' },
  { value: 'has_any_value', label: 'tem algum valor' },
]

const LIST_OPERATORS = [
  { value: 'is', label: 'é' },
  { value: 'is_not', label: 'não é' },
  { value: 'is_unknown', label: 'é desconhecido' },
  { value: 'has_any_value', label: 'tem algum valor' },
]

function getOperatorsForType(type: string) {
  switch (type) {
    case 'text': return TEXT_OPERATORS
    case 'date': return DATE_OPERATORS
    case 'numeric': return NUMERIC_OPERATORS
    case 'boolean': return BOOLEAN_OPERATORS
    case 'list': return LIST_OPERATORS
    default: return TEXT_OPERATORS
  }
}

function transformFieldsToFilterable(fields: Record<string, FieldConfig>): FilterableField[] {
  const labels: Record<string, string> = {
    name: 'Nome',
    email: 'E-mail',
    company_name: 'Empresa',
    created_at: 'Criado em',
    signed_up_at: 'Data de cadastro',
    last_seen_at: 'Última visita',
    conversations_count: 'Conversas',
    events_count: 'Eventos',
  }

  return Object.entries(fields).map(([key, config]) => ({
    key,
    label: config.label || labels[key] || key,
    type: config.type as FilterableField['type'],
    operators: getOperatorsForType(config.type),
    custom: config.custom || false,
    listOptions: config.list_options || undefined,
  }))
}

export default function OutboundMessagesForm({
  outbound_message,
  filterable_fields,
  currentAgent,
  agents,
  default_reaction_emojis,
  max_reaction_emojis,
}: OutboundMessagesFormProps) {
  const { flash } = usePage<OutboundMessagesFormProps>().props
  const isEditing = !!outbound_message

  // For email messages, use email_subject as title if title is empty
  const initialTitle = outbound_message?.message_type === 'email' && !outbound_message?.title
    ? outbound_message?.email_subject || ''
    : outbound_message?.title || ''

  const { data, setData, post, put, processing, errors } = useForm({
    name: outbound_message?.name || '',
    message_type: outbound_message?.message_type || 'chat',
    delivery_style: outbound_message?.delivery_style || 'snippet',
    title: initialTitle,
    body: outbound_message?.body || '',
    button_text: outbound_message?.button_text || '',
    button_url: outbound_message?.button_url || '',
    audience_type: outbound_message?.audience_type || 'dynamic',
    audience_rules: outbound_message?.audience_rules || { logic: 'and' as const, filters: [] },
    trigger_type: outbound_message?.trigger_type || 'all_pages',
    trigger_event: outbound_message?.trigger_event || '',
    trigger_page_rules: outbound_message?.trigger_page_rules || [],
    scheduled_at: outbound_message?.scheduled_at || '',
    delay_seconds: outbound_message?.delay_seconds || 0,
    // Kept as a string so the input can be cleared; Rails casts '' to nil (no cap)
    frequency_cap: outbound_message?.frequency_cap?.toString() || '',
    priority: outbound_message?.priority || 0,
    collect_email: outbound_message?.collect_email || false,
    sent_by_agent_id: outbound_message?.sent_by_agent?.id || currentAgent.id,
    post_format: outbound_message?.post_format || 'small',
    reply_type: outbound_message?.reply_type || 'none',
    reaction_emojis: outbound_message?.reaction_emojis?.length
      ? outbound_message.reaction_emojis
      : default_reaction_emojis,
    // Email-specific fields
    email_subject: outbound_message?.email_subject || '',
    email_from_name: outbound_message?.email_from_name || '',
    email_reply_to: outbound_message?.email_reply_to || '',
  })

  const filterableFields = transformFieldsToFilterable(filterable_fields)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (isEditing && outbound_message) {
      put(`/outbound_messages/${outbound_message.id}`)
    } else {
      post('/outbound_messages')
    }
  }

  const addPageRule = () => {
    setData('trigger_page_rules', [
      ...data.trigger_page_rules,
      { match_type: 'contains', value: '' },
    ])
  }

  const updatePageRule = (index: number, field: keyof PageRule, value: string) => {
    const rules = [...data.trigger_page_rules]
    rules[index] = { ...rules[index], [field]: value }
    setData('trigger_page_rules', rules)
  }

  const removePageRule = (index: number) => {
    setData(
      'trigger_page_rules',
      data.trigger_page_rules.filter((_, i) => i !== index)
    )
  }

  return (
    <>
      <Head title={isEditing ? 'Editar Mensagem de Saída' : 'Nova Mensagem de Saída'} />
      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="outbound" />

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 bg-white border-b border-gray-200 flex items-center px-6">
            <Link
              href="/outbound_messages"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mr-4"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <h1 className="text-lg font-semibold text-gray-900">
              {isEditing ? 'Editar Mensagem de Saída' : 'Nova Mensagem de Saída'}
            </h1>
          </header>

          <main className="flex-1 overflow-auto p-6">
            <div className="max-w-6xl">
              {flash?.alert && (
                <div className="mb-6 flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">{flash.alert}</p>
                </div>
              )}

              {flash?.notice && (
                <div className="mb-6 flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">{flash.notice}</p>
                </div>
              )}

              {Object.keys(errors).length > 0 && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-medium text-red-800 mb-2">
                        Corrija os seguintes erros:
                      </h3>
                      <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                        {Object.entries(errors).map(([field, message]) => (
                          <li key={field}>{message}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Message Type Selector - always visible */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tipo de Mensagem
                      </label>
                      <select
                        value={data.message_type}
                        onChange={(e) => setData('message_type', e.target.value as 'chat' | 'post' | 'email')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      >
                        <option value="chat">Chat (permite respostas)</option>
                        <option value="post">Post (anúncio)</option>
                        <option value="email">E-mail</option>
                      </select>
                    </div>
                    <div className="flex-1">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Título <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="name"
                        type="text"
                        value={data.name}
                        onChange={(e) => setData('name', e.target.value)}
                        placeholder="ex: Mensagem de boas-vindas"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none ${
                          errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Post Layout */}
                {data.message_type === 'post' && (
                  <PostFormLayout
                    data={data}
                    setData={setData}
                    errors={errors}
                    agents={agents}
                    maxReactionEmojis={max_reaction_emojis}
                    filterableFields={filterableFields}
                    addPageRule={addPageRule}
                    updatePageRule={updatePageRule}
                    removePageRule={removePageRule}
                  />
                )}

                {/* Chat Layout */}
                {data.message_type === 'chat' && (
                  <ChatFormLayout
                    data={data}
                    setData={setData}
                    errors={errors}
                    agents={agents}
                    filterableFields={filterableFields}
                    addPageRule={addPageRule}
                    updatePageRule={updatePageRule}
                    removePageRule={removePageRule}
                  />
                )}

                {/* Email Layout */}
                {data.message_type === 'email' && (
                  <EmailFormLayout
                    data={data}
                    setData={setData}
                    errors={errors}
                    agents={agents}
                    filterableFields={filterableFields}
                    addPageRule={addPageRule}
                    updatePageRule={updatePageRule}
                    removePageRule={removePageRule}
                  />
                )}

                {/* Submit */}
                <div className="flex justify-end gap-3">
                  <Link
                    href="/outbound_messages"
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </Link>
                  <button
                    type="submit"
                    disabled={processing}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isEditing ? 'Atualizar Mensagem' : 'Criar Mensagem'}
                  </button>
                </div>
              </form>
            </div>
          </main>
        </div>
      </div>
    </>
  )
}
